import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { TouchableOpacity, View } from 'react-native';
import { useTranslation } from '@oolio-group/localization';
import { useNotification } from '@oolio-group/hooks';
import {
  AuditLogTags,
  logger,
  type AuditLogEntry,
} from '@oolio-group/audit-sdk';
import Icon from '../../Icon/Icon';
import ModalTitle from '../../System/ModalTitle/ModalTitle';
import Search from '../../System/Search/Search';
import { useTheme } from '../../../hooks/useTheme';
import { exportLogs } from '../../../utils/exportLogs';
import ActionLogDetail from './ActionLogDetail';
import ActionLogsSummary from './ActionLogsSummary';
import ActionLogsTable from './ActionLogsTable';
import LogSelect, { LogSelectOption } from './LogSelect';
import { makeStyles } from './ActionLogs.styles';

const ITEMS_PER_PAGE = 10;
const SEARCH_DEBOUNCE_MS = 300;

const LEVEL_OPTIONS: LogSelectOption[] = [
  { value: 'all', label: 'All Levels' },
  { value: 'error', label: 'Error' },
  { value: 'warn', label: 'Warning' },
  { value: 'info', label: 'Info' },
];

const TYPE_OPTIONS: LogSelectOption[] = [
  { value: 'all', label: 'All Types' },
  { value: AuditLogTags.PAYMENT, label: 'Payment Actions' },
  { value: AuditLogTags.USER_ACTION, label: 'User Actions' },
  { value: AuditLogTags.DEVICE, label: 'Device Actions' },
  { value: AuditLogTags.EVENT, label: 'Order Events' },
  { value: AuditLogTags.SYSTEM, label: 'System' },
];

export interface ActionLogsModalProps {
  onClose: () => void;
}

const ActionLogsModal: React.FC<ActionLogsModalProps> = ({ onClose }) => {
  const { translate } = useTranslation();
  const { showNotification } = useNotification();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [selectedLog, setSelectedLog] = useState<AuditLogEntry | null>(null);
  const [searchText, setSearchText] = useState('');
  const [level, setLevel] = useState('all');
  const [type, setType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [summary, setSummary] = useState({ errors: 0, warnings: 0, info: 0 });
  const [currentDate, setCurrentDate] = useState('');

  const fetchLogs = useCallback(async () => {
    try {
      const fetchedLogs = await logger.getLogs({
        endDate: new Date(),
        level: level !== 'all' ? (level as AuditLogEntry['level']) : undefined,
        tag: type !== 'all' ? type : undefined,
      });

      const searchLower = searchText.trim().toLowerCase();
      const filteredLogs = searchLower
        ? fetchedLogs.filter(
            log =>
              log.msg.toLowerCase().includes(searchLower) ||
              JSON.stringify(log.context ?? {})
                .toLowerCase()
                .includes(searchLower),
          )
        : fetchedLogs;

      const today = new Date();
      setLogs(filteredLogs);
      setSummary({
        errors: filteredLogs.filter(log => log.level === 'error').length,
        warnings: filteredLogs.filter(log => log.level === 'warn').length,
        info: filteredLogs.filter(log => log.level === 'info').length,
      });
      setCurrentDate(
        `${today.getDate()} ${today.toLocaleString('default', {
          month: 'short',
        })} ${today.getFullYear()} (${filteredLogs.length})`,
      );
    } catch {
      setLogs([]);
      setSummary({ errors: 0, warnings: 0, info: 0 });
      setCurrentDate('No data');
    }
  }, [level, type, searchText]);

  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    setCurrentPage(1);
    debounceRef.current && clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchLogs, SEARCH_DEBOUNCE_MS);
    return () => {
      debounceRef.current && clearTimeout(debounceRef.current);
    };
  }, [fetchLogs]);

  const onExport = useCallback(async () => {
    try {
      const exportedTo = await exportLogs(
        `cds-audit-logs-${Date.now()}.json`,
        JSON.stringify(logs, null, 2),
      );
      showNotification({
        success: true,
        message: `Logs exported to ${exportedTo}`,
      });
    } catch (error) {
      showNotification({
        error: true,
        message: `Failed to export logs: ${
          error instanceof Error ? error.message : String(error)
        }`,
      });
    }
  }, [logs, showNotification]);

  return (
    <View style={styles.container}>
      <ModalTitle
        title={translate('settings.actionLogs')}
        onDismiss={onClose}
      />
      <View style={styles.content}>
        <View style={styles.filters}>
          <LogSelect
            testID="select-level"
            options={LEVEL_OPTIONS}
            selectedValue={level}
            onValueChange={setLevel}
          />
          <LogSelect
            testID="select-type"
            options={TYPE_OPTIONS}
            selectedValue={type}
            onValueChange={setType}
          />
          <Search
            testID="search-logs"
            value={searchText}
            onChangeText={setSearchText}
            placeholder="Search logs by message or order"
            containerStyle={styles.search}
          />
          <TouchableOpacity
            testID="btn-exportLogs"
            style={styles.iconButton}
            onPress={onExport}
          >
            <Icon name="cloud-download" size={20} color={colors.text} />
          </TouchableOpacity>
          <TouchableOpacity
            testID="btn-refreshLogs"
            style={styles.iconButton}
            onPress={fetchLogs}
          >
            <Icon name="sync" size={20} color={colors.text} />
          </TouchableOpacity>
        </View>
        <ActionLogsSummary
          errors={summary.errors}
          warnings={summary.warnings}
          info={summary.info}
          date={currentDate}
        />
        {selectedLog ? (
          <ActionLogDetail
            log={selectedLog}
            onClose={() => setSelectedLog(null)}
          />
        ) : (
          <ActionLogsTable
            currentPage={currentPage}
            data={logs}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setCurrentPage}
            onPressRow={setSelectedLog}
          />
        )}
      </View>
    </View>
  );
};

export default ActionLogsModal;
